import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import type { QuoteItemSection, RenderableItem } from '@paintscout/util/builder'
import { hasCustomTotal } from '@paintscout/util/'
import React, { useState } from 'react'
import type { Item } from '../ItemTable'
import { useQuote } from '../context'
import BulkActions from '../Quote/BulkActions'
import AreaSubstrateToggle from './AreaSubstrateToggle'
import ArchivedTableNext from './ArchivedTableNext'

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {},
      // The card's section strip grows to hold the two-line "Archived" heading + subtitle.
      sectionTitle: {
        padding: theme.spacing(1, 0)
      },
      title: {
        fontWeight: 700,
        fontSize: theme.typography.pxToRem(20),
        lineHeight: 1.2,
        color: neutral.P30
      },
      subtitle: {
        marginTop: theme.spacing(0.5),
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P50
      }
    }
  },
  { name: 'ArchivedItemsViewNext' }
)

/**
 * The Archived tab for the new (estimateNext) editor — a functional rewrite of the legacy
 * class-based `ArchivedItemsView`. Owns the archived-section selection state (surfacing selected
 * keys to the shared context so the floating `BulkActions` bar shows), the card's section header
 * ("Archived" title + subtitle + Area/Surface toggle), and renders the redesigned `ArchivedTableNext`.
 */
export default function ArchivedItemsViewNext() {
  const classes = useStyles()
  const [selectedItems, setSelectedItems] = useState<RenderableItem[]>([])
  const { options } = useClientOptions()
  const { quote, tableView, isEditable, onItemAction, onTableViewChange, setSelectedItemKeys } = useQuote()

  const disableSubstrateView = hasCustomTotal(quote.areas, true)
  const view = disableSubstrateView ? 'area' : tableView

  const sectionTitle = (
    <div className={classes.sectionTitle}>
      <Typography className={classes.title}>Archived</Typography>
      <Typography className={classes.subtitle}>These items will not show anywhere except here.</Typography>
    </div>
  )

  const sectionAction = isEditable ? (
    <AreaSubstrateToggle view={view} onChange={handleTableViewChange} disabled={disableSubstrateView} dropdown />
  ) : undefined

  return (
    <div className={classes.root}>
      <ArchivedTableNext
        sectionTitle={sectionTitle}
        sectionAction={sectionAction}
        selectedItems={selectedItems}
        onItemSelection={handleItemSelection}
        onItemAction={handleItemAction}
      />
      {isEditable && (
        <BulkActions
          options={options}
          quote={quote}
          tableView={view}
          getSelectedItems={getSelectedItems}
          getSelectedItemsByType={getSelectedItemsByType}
          onBulkActionClick={handleBulkActionClick}
          onDeselectAll={handleDeselectAll}
        />
      )}
    </div>
  )

  function getSelectedItems(args?: { section?: QuoteItemSection }): RenderableItem[] {
    // Only the archived section is selectable on this tab; every other section is empty.
    if (args?.section && args.section !== 'archived') {
      return []
    }
    return selectedItems
  }

  function getSelectedItemsByType(type: string): RenderableItem[] {
    return selectedItems.filter((item) => item.type === type)
  }

  function handleItemSelection(_event: React.ChangeEvent<HTMLElement>, items: Item[]) {
    const next = items as RenderableItem[]
    setSelectedItems(next)
    setSelectedItemKeys?.(next.map((item) => item.key))
  }

  function handleItemAction(action: string, section: 'archived', item: RenderableItem) {
    const moveActions = [
      'make-option',
      'archive',
      'unarchive',
      'move-to-bid',
      'move-to-additional',
      'move-to-pending',
      'delete'
    ]
    if (moveActions.includes(action)) {
      deselect(item.key)
    }
    onItemAction?.(action, section, [item.key])
  }

  function handleBulkActionClick(action: string) {
    return (items: RenderableItem[]) => {
      onItemAction?.(
        action,
        'archived',
        items.map((item) => item.key)
      )
      resetSelected()
    }
  }

  function handleDeselectAll() {
    resetSelected()
  }

  function handleTableViewChange(_event: any, nextView: string) {
    onTableViewChange?.(nextView)
    resetSelected()
  }

  function deselect(key: string) {
    setSelectedItems((prev) => {
      const next = prev.filter((item) => item.key !== key)
      setSelectedItemKeys?.(next.map((item) => item.key))
      return next
    })
  }

  function resetSelected() {
    setSelectedItems([])
    setSelectedItemKeys?.([])
  }
}
